import { Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import versionService, { VersionInfo } from "../../../api/VersionService";
import {
  borderStyle,
  boxHeight,
  checkboxGap,
  miniGap,
  perelynGrey,
  perelynPrimary,
  smallerGap,
  smallGap,
} from "../../../styles/consts";
import {
  getFormattedName,
  getUserInitials,
  hasSalesOrAdminAccess,
  splitMail,
} from "../../../utils";
import { useUser } from "../../context/UserProvider";
import { OrangeOnHoverStack } from "../../styles/Box.styles";
import {
  MainSidebarComponent,
  MiddleSidebarComponent,
} from "../../styles/Sidebar.styles";
import Icon, { BiggerIcon } from "../fundamentals/Icon";
import Logout from "../user/Logout";

export default function PrimarySidebar() {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState<VersionInfo | null>(null);

  useEffect(() => {
    versionService.getVersion().then(setVersion);
  }, []);

  const handleUserSettings = () => {
    navigate("/user-settings");
  };

  const handleAdminSettings = async () => {
    navigate("/admin-settings");
  };

  const handleReturn = () => {
    // Check if we're in an archive route
    if (location.pathname.startsWith("/archive")) {
      navigate("/archive");
    } else {
      navigate("/alqemi");
    }
  };

  const handleGoToAlqemi = () => {
    navigate("/alqemi");
  };

  return (
    <MainSidebarComponent sx={{ borderRight: borderStyle }}>
      {/* Version display at the top */}
      {version && (
        <Stack
          sx={{
            padding: smallGap,
            borderBottom: borderStyle,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: perelynGrey,
              fontFamily: "monospace",
            }}
          >
            v{version.git_commit}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.625rem",
              color: perelynGrey,
              opacity: 0.7,
            }}
          >
            {version.environment.toUpperCase()}
          </Typography>
        </Stack>
      )}

      <MiddleSidebarComponent>
        <Stack sx={{ gap: miniGap }}>
          {hasSalesOrAdminAccess(user) && (
            <OrangeOnHoverStack
              id="analytics"
              onClick={() => navigate("/analytics")}
            >
              <Icon name="Chart" />
              Analytics
            </OrangeOnHoverStack>
          )}
          <OrangeOnHoverStack id="archive" onClick={() => navigate("/archive")}>
            <Icon name="Archive" />
            Archive
          </OrangeOnHoverStack>
          <OrangeOnHoverStack id="settings" onClick={handleUserSettings}>
            <Icon name="Settings" />
            Settings
          </OrangeOnHoverStack>
          {user.profile === "admin" && (
            <OrangeOnHoverStack
              id="admin-settings"
              onClick={handleAdminSettings}
            >
              <Icon name="Settings" />
              Admin Settings
            </OrangeOnHoverStack>
          )}
        </Stack>
      </MiddleSidebarComponent>
      {location.pathname === "/archive" && (
        <Button
          variant="transparentBig"
          id="go-to-alqemi"
          sx={{
            borderTop: borderStyle,
            gap: checkboxGap,
          }}
          onClick={handleGoToAlqemi}
        >
          Go back to Alqemi
          <BiggerIcon
            sx={{
              width: smallerGap,
              height: smallerGap,
              minHeight: smallerGap,
              maxHeight: smallerGap,
            }}
            name="Return"
          />
        </Button>
      )}
      {location.pathname !== "/alqemi" && location.pathname !== "/archive" && (
        <Button
          variant="transparentBig"
          id="return"
          sx={{
            borderTop: borderStyle,
            gap: checkboxGap,
          }}
          onClick={handleReturn}
        >
          Go back to dashboard
          <BiggerIcon
            sx={{
              width: smallerGap,
              height: smallerGap,
              minHeight: smallerGap,
              maxHeight: smallerGap,
            }}
            name="Return"
          />
        </Button>
      )}
      <Stack
        sx={{
          borderTop: borderStyle,
          flexDirection: "row",
          justifyContent: "space-between",
          height: boxHeight,
          boxSizing: "border-box",
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            gap: "0.75rem",
            boxSizing: "border-box",
            padding: `0.75rem 0rem 0.75rem ${smallGap}`,
          }}
        >
          <Stack
            sx={{
              height: "2.5rem",
              width: "2.5rem",
              border: `1px solid ${perelynPrimary}`,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ color: perelynPrimary, fontSize: smallGap }}>
              {getUserInitials(user)}
            </Typography>
          </Stack>
          <Stack>
            <Typography sx={{ fontSize: "1.125rem" }}>
              {getFormattedName(user.name)}
            </Typography>
            <Typography>{splitMail(user.email)}</Typography>
          </Stack>
        </Stack>
        <Logout />
      </Stack>
    </MainSidebarComponent>
  );
}
